import { createPortal, useEffect, useState } from "preact/compat";
import ADAIcon from "./icon/ADAIcon";
import { Button, Drawer } from "antd";
import imageService from "../service/imageService";
import util, { syncUploadedIconsAltTextOnCanvas, syncUploadedImagesAltTextOnCanvas } from "../util";
import constants from "../constants";
import { CloseOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import AltTextHelpModal from "./AltTextHelpModal";
import store from '../store';
import * as actions from "../actions";

export interface AltTextModalProps {
  open: boolean;
  onClose: () => void;
  isMobile: boolean;
  $element: JQuery;
  selectedUploadedImage: any;
  selectedUploadedIcon: any;
  uploadedImages: any[];
  setUploadedImages: (uploadedImages: any[]) => void;
  uploadedIcons: any[];
  setUploadedIcons: (uploadedIcons: any[]) => void;
}

export default function AltTextModal({ open, onClose, isMobile, $element, selectedUploadedImage, selectedUploadedIcon, uploadedImages, setUploadedImages, uploadedIcons, setUploadedIcons }: AltTextModalProps) {
  const [curText, setCurText] = useState('');
  const [imgUrl, setImgUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('icon');
  const [isHelpModal, setHelpModal] = useState(false);

  function setAltText() {
    if ($element && $element.hasClass('frame')) {
      const $imgEl = $element.find('.img');
      $imgEl.attr('role', 'img');
      $imgEl.attr('aria-label', curText);
    }

    if ($element && $element.attr('data-component') === 'video-frame') {
      const $videoEl = $element.find('video');
      $videoEl.attr('role', 'img');
      $videoEl.attr('aria-label', curText);
    }

    if (mode === 'frame' || mode === 'image' || mode === 'video') {
      if (imgUrl.includes('https://timber.mhmcdn.com/') || imgUrl.includes('https://mhm-oo.s3.amazonaws.com/')) { //is an uploaded image
        setLoading(true);
        const imgPath = imgUrl.includes('https://timber.mhmcdn.com/') ? imgUrl.split('https://timber.mhmcdn.com/')[1] : imgUrl.split('https://mhm-oo.s3.amazonaws.com/')[1];
        imageService.updateImageAltText(imgPath, curText).then(resp => {
          const uploadedImgsCopy = [...uploadedImages];
          const toUpdate = uploadedImgsCopy.find(i => i.path === imgPath);
          if (toUpdate) {
            toUpdate.altText = curText;
            setUploadedImages([...uploadedImgsCopy]);
            syncUploadedImagesAltTextOnCanvas([...uploadedImgsCopy]);
          }
          setLoading(false);
          resetAndClose();
        }).catch(err => {
          console.log(err);
          setLoading(false);
          resetAndClose();
        })
      } else {
        resetAndClose();
      }
    } else if (mode === 'icon') { //is an uploaded icon
      setLoading(true);
      imageService.updateIconAltText(selectedUploadedIcon.id, curText).then(resp => {
        const uploadedIconsCopy = [...uploadedIcons];
        const toUpdate = uploadedIconsCopy.find(i => i.id === selectedUploadedIcon.id);
        if (toUpdate) {
          toUpdate.altText = curText;
          setUploadedIcons([...uploadedIconsCopy]);
          syncUploadedIconsAltTextOnCanvas([...uploadedIconsCopy]);
        }
        setLoading(false);
        resetAndClose();
      }).catch(err => {
        console.log(err);
        setLoading(false);
        resetAndClose();
      })
    } else {
      resetAndClose();
    }

  }

  useEffect(() => {
    if ($element && $element.hasClass('frame')) {
      const $imgEl = $element.find('.img');
      if ($imgEl.length) {
        setMode('frame');
        const url = $imgEl.css('background').split('"')[1];
        setImgUrl(url);
        if ($imgEl.attr('aria-label')) {
          setCurText($imgEl.attr('aria-label')!);
        } else {
          setCurText('');
        }
      }
    }

    if ($element && $element.attr('data-component') === 'video-frame') {
      const $videoEl = $element.find('video');
      if ($videoEl.length) {
        setMode('video');
        const url = $videoEl.attr('src')!;
        setImgUrl(url);
        if ($videoEl.attr('aria-label')) {
          setCurText($videoEl.attr('aria-label')!);
        } else {
          setCurText('');
        }
      }
    }
  }, [$element])

  useEffect(() => {
    if (!selectedUploadedImage || !selectedUploadedImage.id) return;
    if (selectedUploadedImage.isMp4 || selectedUploadedImage.isWebM) {
      setMode('video');
    } else {
      setMode('image');
    }
    setImgUrl(util.getUploadedImgSrc(selectedUploadedImage));
    if (selectedUploadedImage.altText) {
      setCurText(selectedUploadedImage.altText);
    } else {
      setCurText('');
    }
  }, [selectedUploadedImage])

  useEffect(() => {
    if (!selectedUploadedIcon || !selectedUploadedIcon.id) return;
    setMode('icon');
    setImgUrl(util.getUploadedIconPath(selectedUploadedIcon));
    if (selectedUploadedIcon.altText) {
      setCurText(selectedUploadedIcon.altText);
    } else {
      setCurText('');
    }
  }, [selectedUploadedIcon])

  function deleteUploadedIcon() {
    (window as any).ConfirmationModal.show({
      title: 'Are you sure?',
      body: 'This icon upload will be removed from your gallery.',
      acceptLabel: 'Ok',
      declineLabel: 'Cancel',
      onAccept: () => {
        setLoading(true);
        imageService.removeMemberIcon(selectedUploadedIcon.id).then(response => {
          setUploadedIcons(uploadedIcons.filter(it => it.id !== selectedUploadedIcon.id));
          setLoading(false);
          resetAndClose();
        })
      },
      onDecline: () => {
        (window as any).ConfirmationModal.hide();
      }
    })
  }

  function resetAndClose() {
    store.dispatch(actions.setIsJustUploaded(false));
    onClose();
  }

  const modalContent = <div className="content">
    <div className="img-cont">
      {mode === 'video' &&
      <video loop muted playsInline src={imgUrl} style="width: 100%; height: 100%; object-fit: cover;">
      </video>}
      {mode !== 'video' &&
      <img src={imgUrl} alt="current image to add alt text" />}
    </div>
    <h3>
      <ADAIcon /> 
      Accessibility Text 
      <div className="help-btn hint--bottom" data-hint="What's this?" onClick={() => setHelpModal(true)}><QuestionCircleOutlined/></div>
    </h3>
    <p>Image description necessary for ADA compliance and screen readers</p>
    <textarea placeholder="Description 125 chars max" maxLength={125} value={curText} onChange={(e: any) => setCurText(e.target.value)} />
    <Button type="primary" className={`btn primary`} disabled={!curText} loading={loading} onClick={setAltText}>Done</Button>
    {mode === 'icon' && !store.getState().isJustUploaded &&
    <Button className={`btn skip`} loading={loading} onClick={() => deleteUploadedIcon()}>Delete icon</Button>}
    {(mode === 'icon' || mode === 'image') && store.getState().isJustUploaded &&
    <Button className={`btn skip`} loading={loading} onClick={() => resetAndClose()}>Skip for now</Button>}
  </div>

  return open ? 
  isMobile ?
  <Drawer
    className="alt-text-drawer"
    open={open} 
    onClose={onClose} 
    closeIcon={<CloseOutlined/>} 
    placement="bottom" 
    zIndex={120} 
    height={'75vh'}
    push={false}>
    {modalContent}

    <AltTextHelpModal open={isHelpModal} onClose={() => setHelpModal(false)} isMobile={isMobile} />
  </Drawer>
  :
  createPortal(<div>
    <div className="modal-back" onClick={resetAndClose} /> 
    <div className="modal alt-text-modal" onClick={e => e.stopPropagation()}>
      <div onClick={resetAndClose} className="close-btn"><CloseOutlined/></div>
      {modalContent}
    </div>
    <AltTextHelpModal open={isHelpModal} onClose={() => setHelpModal(false)} isMobile={isMobile} />
  </div>, document.getElementById('modals')!) : null;
}
